import React, {useMemo} from "react";
import {Autocomplete, TextField, Chip} from "@mui/material";
import PropTypes from "prop-types";

function SearchableSelect({
  options = [],
  value,
  onChange = () => {},
  label = "",
  multiple = false,
  disabled = false,
  valueKey = "id",
  getOptionLabel: getOptionLabelProp,
}) {
  const isStringOptions = options.length > 0 && typeof options[0] === "string";

  const getOptionLabel = isStringOptions
    ? (o) => (typeof o === "string" ? o : "")
    : getOptionLabelProp || ((o) => o.title || "");

  const resolvedValue = useMemo(() => {
    if (isStringOptions) {
      return multiple ? (value || []) : (value || null);
    }
    if (multiple) {
      const ids = value || [];
      return ids.map((id) => options.find((o) => o[valueKey] === id)).filter(Boolean);
    }
    if (!value) return null;
    return options.find((o) => o[valueKey] === value) || null;
  }, [value, options, multiple, isStringOptions, valueKey]);

  const handleChange = (_event, newValue) => {
    if (isStringOptions) {
      onChange(multiple ? (newValue || []) : (newValue || ""));
      return;
    }
    if (multiple) {
      onChange((newValue || []).map((o) => o[valueKey]));
    } else {
      onChange(newValue ? newValue[valueKey] : "");
    }
  };

  const isOptionEqualToValue = isStringOptions
    ? (option, val) => option === val
    : (option, val) => option[valueKey] === val[valueKey];

  return (
    <Autocomplete
      multiple={multiple}
      disabled={disabled}
      options={options}
      value={resolvedValue}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={(props, option) => (
        <li {...props} style={{color: "#000", backgroundColor: "#fff"}}>
          {getOptionLabel(option)}
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={label} />}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const tagProps = getTagProps({index});
          return (
            <Chip
              key={tagProps.key || `${getOptionLabel(option)}-${index}`}
              label={getOptionLabel(option)}
              size="small"
              {...tagProps}
            />
          );
        })
      }
      size="small"
      fullWidth
    />
  );
}

SearchableSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
  ]),
  onChange: PropTypes.func,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  valueKey: PropTypes.string,
  getOptionLabel: PropTypes.func,
};

export default SearchableSelect;
